import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { logout } from '../../store/slices/authSlice';
import { fetchUnreadNotificationsCount } from '../../store/slices/negotiationsSlice';
import { fetchNotifications as fetchGiftNotifications } from '../../store/slices/giftCardsSlice';
import { fetchCart, clearCart } from '../../store/slices/cartSlice';
import SideMenu, { HamburgerButton, HamburgerLine } from './SideMenu';
import LanguageSwitcher from '../../i18n/components/LanguageSwitcher';
import PointsAuraCorner from '../points/PointsAuraCorner';
import styled from 'styled-components';

const HeaderContainer = styled.header`
  font-family: 'Manrope', sans-serif;
  background-color: #faf8f4;
  border-bottom: 1px solid #e8e0d4;
  position: sticky;
  top: 0;
  z-index: 100;
  overflow: visible;
`;

const AnnouncementBar = styled.div`
  background: #1b4332;
  color: #faf8f4;
  text-align: center;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  padding: 0.45rem 1rem;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 0.58rem;
    letter-spacing: 0.08em;
    padding: 0.38rem 0.65rem;
  }
`;

const HamburgerWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  /* DOM is after UserMenu; keep hamburger beside logo on desktop */
  order: 2;
  margin-left: -0.15rem;
  margin-right: 0.35rem;

  @media (max-width: 768px) {
    /* Far left on mobile, before logo */
    order: 0;
    margin-left: -0.15rem;
    margin-right: 0.15rem;
  }
`;

const Nav = styled.nav`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 0.75rem 0 0.5rem;
  display: flex;
  align-items: center;
  height: 62px;
  gap: 0.35rem;
  overflow: visible;
  position: relative;

  @media (max-width: 768px) {
    height: 50px;
    padding: 0 0.45rem;
    gap: 0.25rem;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  transition: opacity 0.2s ease;
  flex-shrink: 0;
  margin-right: 0.25rem;
  z-index: 10;
  order: 1;
  
  img {
    height: 32px;
    width: 110px;
    max-width: 110px;
    object-fit: contain;
    border-radius: 4px;
  }

  @media (max-width: 768px) {
    margin-right: 0.1rem;
    img {
      height: 24px;
      width: 80px;
      max-width: 80px;
    }
  }
  
  &:hover {
    opacity: 0.8;
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(0.45rem, 0.9vw, 1rem);
  flex-wrap: nowrap;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  padding-left: 0.15rem;
  padding-right: 0.75rem;
  order: 3;
  
  @media (max-width: 1200px) {
    gap: 0.4rem;
  }
  
  @media (max-width: 1100px) {
    gap: 0.35rem;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: #1b4332;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.68rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  transition: color 0.2s ease, opacity 0.2s ease;
  white-space: nowrap;
  flex-shrink: 1;
  padding: 0 0.15rem;
  
  &:hover {
    color: #2d6a4f;
    opacity: 0.85;
  }
  
  @media (min-width: 1280px) {
    font-size: 0.72rem;
    padding: 0 0.3rem;
    flex-shrink: 0;
  }
`;

const LoginNavButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.4rem 0.85rem;
  border: 2px solid var(--primary-color);
  border-radius: 0.375rem;
  background: transparent;
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.875rem;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover {
    background-color: var(--primary-color);
    color: #fff;
  }

  @media (max-width: 768px) {
    padding: 0.28rem 0.55rem;
    font-size: 0.75rem;
    border-width: 1.5px;
  }
`;

const CreateAccountNavButton = styled(LoginNavButton)`
  background-color: var(--primary-color);
  color: #fff;

  &:hover {
    background-color: var(--primary-dark, #059669);
    border-color: var(--primary-dark, #059669);
    color: #fff;
  }
`;

const GuestAuthButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    gap: 0.25rem;
  }
`;

const UserMenu = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.2rem;
  flex-shrink: 0;
  margin-left: auto;
  padding-left: 0.35rem;
  z-index: 10;
  min-width: 0;
  min-height: 40px;
  justify-content: flex-end;
  order: 4;

  @media (max-width: 768px) {
    gap: 0.2rem;
    padding-left: 0.2rem;
    min-width: 7.5rem;
    min-height: 34px;
    margin-left: auto;
    margin-right: 0;
    order: 3;
  }
`;

const AuthChromePlaceholder = styled.div`
  width: 10.5rem;
  height: 36px;
  border-radius: 0.5rem;
  background: linear-gradient(90deg, #eef0f3 0%, #f7f8fa 50%, #eef0f3 100%);
  background-size: 800px 100%;
  animation: headerAuthShimmer 1.4s infinite linear;
  flex-shrink: 0;

  @keyframes headerAuthShimmer {
    0% { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }

  @media (max-width: 768px) {
    width: 6.5rem;
    height: 32px;
  }
`;

const IconButton = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: var(--text-secondary);
  transition: color 0.2s ease;
  
  &:hover {
    color: var(--primary-color);
  }

  @media (max-width: 768px) {
    display: ${(props) => (props.$hideOnMobile ? 'none' : 'flex')};
  }
`;

const IconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;

  @media (max-width: 768px) {
    width: 34px;
    height: 34px;
  }
`;

const Icon = styled.span`
  font-size: 1.5rem;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const PlusIcon = styled.span`
  font-size: 1.65rem;
  font-weight: 400;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 1.35rem;
  }
`;

const Badge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  background-color: var(--primary-color);
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  border: 2px solid var(--bg-primary);
`;

const UserButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 11rem;
  min-height: 36px;
  padding: 0.25rem 0.35rem;
  border-radius: 0.375rem;
  
  &:hover {
    color: var(--primary-color);
  }

  @media (max-width: 768px) {
    max-width: 6.5rem;
    gap: 0.2rem;
    padding: 0.2rem 0.3rem;
    font-size: 0.78rem;
  }
`;

const UserButtonLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CartNavButton = styled(Link)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.1rem;
  text-decoration: none;
  color: var(--text-secondary);
  min-width: 2.75rem;
  padding: 0.1rem 0.2rem;
  transition: color 0.2s ease;

  &:hover {
    color: var(--primary-color);
  }

  @media (max-width: 768px) {
    min-width: 2rem;
    padding: 0;
  }
`;

const CartNavLabel = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.02em;
  text-transform: none;

  @media (max-width: 768px) {
    display: none;
  }
`;

const AccountCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    gap: 0.25rem;
  }
`;

const UserDropdownWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PointsAuraWrap = styled.div`
  /* Badge is position:fixed; keep a zero-size mount point in the header tree. */
  width: 0;
  height: 0;
  overflow: visible;
  flex-shrink: 0;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  box-shadow: var(--shadow-lg);
  min-width: 200px;
  z-index: 1000;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const DropdownItem = styled(Link)`
  display: block;
  padding: 0.75rem 1rem;
  color: var(--text-secondary);
  text-decoration: none;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: var(--bg-secondary);
    color: var(--primary-color);
  }
`;

const DropdownButton = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  color: var(--error-color);
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: var(--bg-secondary);
  }
`;

const DepartmentBar = styled.div`
  border-top: 1px solid var(--border-color);
  background-color: var(--bg-primary);
`;

const DepartmentBarInner = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0.5rem 0.75rem 0.6rem 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    padding: 0.45rem 0.45rem 0.55rem;
    flex-direction: column;
    align-items: stretch;
    gap: 0.4rem;
  }
`;

const DepartmentLabel = styled.span`
  color: var(--text-secondary);
  font-size: 0.82rem;
  font-weight: 600;
  white-space: nowrap;
`;

const DepartmentChips = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const DepartmentChip = styled(Link)`
  text-decoration: none;
  border: 1px solid ${(props) => (props.$active ? 'var(--primary-color)' : 'var(--border-color)')};
  background-color: ${(props) => (props.$active ? 'var(--primary-color)' : 'var(--bg-primary)')};
  color: ${(props) => (props.$active ? '#fff' : 'var(--text-primary)')};
  border-radius: 999px;
  padding: 0.4rem 0.85rem;
  font-size: 0.86rem;
  font-weight: 600;
  line-height: 1.1;
  white-space: nowrap;
  transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease;
  min-height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: var(--primary-color);
    color: ${(props) => (props.$active ? '#fff' : 'var(--primary-color)')};
  }
`;

const DEPARTMENT_LINKS = [
  { value: 'Men', labelKey: 'nav.departmentMen' },
  { value: 'Women', labelKey: 'nav.departmentWomen' },
  { value: 'Kids', labelKey: 'nav.departmentKids' },
  { value: 'Babies', labelKey: 'nav.departmentBabies' },
];

const DEPARTMENT_BROWSE_BASE_PATHS = [
  '/pre-owned-items',
  '/defected-items',
  '/hobbies-finds',
  '/luxury',
  '/boutiques',
  '/designers',
  '/artists',
];

const Header = () => {
  const { isAuthenticated, user, sessionChecked } = useSelector((state) => state.auth);
  const cart = useSelector((state) => state.cart);
  const { unreadCount: rawUnreadCount } = useSelector((state) => state.negotiations);
  const unreadCount = Number(rawUnreadCount) || 0;
  const giftUnreadCount = Number(useSelector((state) => state.giftCards.unreadCount)) || 0;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const createItemPath = (() => {
    if (location.pathname === '/defected-items') return '/create-item?defected=1';
    if (location.pathname === '/hobbies-finds') return '/create-item?group=hobbies';
    if (
      location.pathname === '/wedding-essentials'
      || location.pathname.startsWith('/wedding-essentials/')
      || location.pathname === '/create-wedding-pre-loved-item'
    ) {
      return '/create-item?group=wedding';
    }
    if (
      location.pathname === '/luxury'
      || location.pathname.startsWith('/luxury/')
      || location.pathname === '/list-luxury-piece'
    ) {
      return '/list-luxury-piece';
    }
    return '/create-item';
  })();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const departmentParams = new URLSearchParams(location.search);
  const departmentFromQuery = departmentParams.get('department');
  const legacyDepartmentFromClothing =
    location.pathname === '/pre-owned-items' && departmentParams.get('category') === 'Clothing'
      ? departmentParams.get('subcategory')
      : '';
  const activeDepartment = departmentFromQuery || legacyDepartmentFromClothing;
  
  // Calculate cart items count
  const cartItemsCount = cart?.items?.length || 0;

  // Single poller for negotiation + gift badges. Pause while the tab is hidden
  // so background YouTube/etc. tabs don't keep hitting the API every 30s.
  useEffect(() => {
    if (!isAuthenticated || typeof document === 'undefined') {
      return undefined;
    }

    let intervalId = null;

    const pollOnce = () => {
      dispatch(fetchUnreadNotificationsCount());
      dispatch(fetchGiftNotifications());
    };

    const stopPolling = () => {
      if (intervalId != null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const startPolling = () => {
      stopPolling();
      pollOnce();
      intervalId = setInterval(pollOnce, 30000);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        stopPolling();
      } else {
        startPolling();
      }
    };

    if (document.visibilityState === 'visible') {
      startPolling();
    }

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [dispatch, isAuthenticated]);

  // Fetch cart once on login for the badge. Refresh only after add/remove/checkout
  // mutations (and Cart/Checkout page mounts) — no background polling.
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchCart());
      return undefined;
    }
    dispatch(clearCart());
    return undefined;
  }, [dispatch, isAuthenticated]);
  
  // Whitelisted courier emails - only these users can see the courier tab
  const COURIER_WHITELIST = [
    'ranakhms@gmail.com',
    // Add more courier emails here as needed
  ];
  
  // Whitelisted admin emails - only these users can see the admin dashboard
  const ADMIN_WHITELIST = [
    'flea.eg@gmail.com',
    // Add more admin emails here as needed
  ];
  
  // Check if current user is a whitelisted courier
  const isCourier = isAuthenticated && user?.email && COURIER_WHITELIST.includes(user.email.toLowerCase());
  
  // Check if current user is a whitelisted admin
  const isAdmin = isAuthenticated && user?.email && ADMIN_WHITELIST.includes(user.email.toLowerCase());
  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
    setDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setSideMenuOpen(false);
    setDropdownOpen(!dropdownOpen);
  };

  const resolveDepartmentBasePath = () => {
    const currentPath = location.pathname || '';
    const matched = DEPARTMENT_BROWSE_BASE_PATHS.find(
      (basePath) => currentPath === basePath || currentPath.startsWith(`${basePath}/`)
    );
    return matched || '/pre-owned-items';
  };

  const getDepartmentHref = (department) => {
    const params = new URLSearchParams();
    params.set('department', department);
    return `${resolveDepartmentBasePath()}?${params.toString()}`;
  };

  // Wedding Essentials has its own Men/Women department bar — hide the global Kids/Babies strip there.
  // Home landing is a focused composition — hide the department strip there too.
  const showGlobalDepartmentBar = !(
    location.pathname === '/'
    || location.pathname === '/wedding-essentials'
    || location.pathname.startsWith('/wedding-essentials/')
  );

  return (
    <HeaderContainer>
      <AnnouncementBar>
        {t('home.announcementBar', {
          defaultValue: 'Try on at the door · Flea delivers · Wallet payouts 3× weekly',
        })}
      </AnnouncementBar>
      <Nav $isAuthenticated={isAuthenticated}>
        <Logo to="/" $isAuthenticated={isAuthenticated}>
          <img
            src="/images/flea-logo.png?v=5"
            alt="FLEA"
            width={96}
            height={28}
            decoding="async"
          />
        </Logo>
        <NavLinks $isAuthenticated={isAuthenticated}>
          <NavLink to="/pre-owned-items">{t('nav.preOwned')}</NavLink>
          <NavLink to="/designers">{t('nav.designers')}</NavLink>
          <NavLink to="/boutiques">{t('nav.boutiques')}</NavLink>
          <NavLink to="/artists">{t('nav.artists')}</NavLink>
          <NavLink to="/wedding-essentials">{t('nav.weddings')}</NavLink>
          {isAuthenticated && isAdmin && <NavLink to="/admin">{t('nav.admin')}</NavLink>}
        </NavLinks>

        <UserMenu $isAuthenticated={isAuthenticated}>
          {!sessionChecked ? (
            <AuthChromePlaceholder aria-hidden />
          ) : isAuthenticated ? (
            <>
              <IconButton to={createItemPath} aria-label="Offer Item">
                <IconWrapper>
                  <PlusIcon>+</PlusIcon>
                </IconWrapper>
              </IconButton>
              <IconButton to="/favorites" aria-label="Favorites" $hideOnMobile>
                <IconWrapper>
                  <Icon>❤️</Icon>
                </IconWrapper>
              </IconButton>
              <IconButton to="/giftcards" aria-label="Gift cards" $hideOnMobile>
                <IconWrapper>
                  <Icon>🎁</Icon>
                  {giftUnreadCount > 0 && (
                    <Badge>{giftUnreadCount > 99 ? '99+' : giftUnreadCount}</Badge>
                  )}
                </IconWrapper>
              </IconButton>
              <PointsAuraWrap>
                <PointsAuraCorner />
              </PointsAuraWrap>
              <IconButton to="/negotiations" aria-label="Messages" $hideOnMobile>
                <IconWrapper>
                  <Icon>✉️</Icon>
                  {unreadCount > 0 && (
                    <Badge>{unreadCount > 99 ? '99+' : unreadCount}</Badge>
                  )}
                </IconWrapper>
              </IconButton>
              <AccountCluster>
                <LanguageSwitcher compact />
                <UserDropdownWrap>
                  <UserButton
                    type="button"
                    onClick={toggleDropdown}
                    aria-expanded={dropdownOpen}
                    aria-haspopup="menu"
                    aria-label={t('nav.profile')}
                  >
                    <UserButtonLabel>
                      {user?.name || user?.username || 'User'}
                    </UserButtonLabel>
                    <span aria-hidden>▼</span>
                  </UserButton>
                  <DropdownMenu isOpen={dropdownOpen} role="menu">
                    <DropdownItem to="/profile" onClick={() => setDropdownOpen(false)}>{t('nav.profile')}</DropdownItem>
                    <DropdownItem to="/my-catalogue" onClick={() => setDropdownOpen(false)}>{t('nav.myCatalogue')}</DropdownItem>
                    <DropdownItem to="/orders" onClick={() => setDropdownOpen(false)}>{t('nav.orders')}</DropdownItem>
                    <DropdownItem to="/wallet-balance-and-aura" onClick={() => setDropdownOpen(false)}>{t('nav.auraWallet')}</DropdownItem>
                    <DropdownItem to="/giftcards" onClick={() => setDropdownOpen(false)}>{t('nav.gifts')}</DropdownItem>
                    <DropdownItem to="/favorites" onClick={() => setDropdownOpen(false)}>{t('nav.favoritesHeart')}</DropdownItem>
                    <DropdownItem to="/negotiations" onClick={() => setDropdownOpen(false)}>{t('nav.messages')}</DropdownItem>
                    <DropdownItem to="/interests" onClick={() => setDropdownOpen(false)}>{t('nav.interests')}</DropdownItem>
                    <DropdownItem to="/what-to-offer" onClick={() => setDropdownOpen(false)}>{t('nav.whatToOffer')}</DropdownItem>
                    {isCourier && <DropdownItem to="/courier" onClick={() => setDropdownOpen(false)}>{t('nav.courier')}</DropdownItem>}
                    {isAdmin && <DropdownItem to="/admin" onClick={() => setDropdownOpen(false)}>{t('nav.adminDashboard')}</DropdownItem>}
                    <DropdownButton onClick={handleLogout}>
                      {t('nav.logout')}
                    </DropdownButton>
                  </DropdownMenu>
                </UserDropdownWrap>
                <CartNavButton to="/cart" aria-label={t('nav.cart')}>
                  <IconWrapper>
                    <Icon>🛒</Icon>
                    {cartItemsCount > 0 && (
                      <Badge>{cartItemsCount > 99 ? '99+' : cartItemsCount}</Badge>
                    )}
                  </IconWrapper>
                  <CartNavLabel>{t('nav.cart')}</CartNavLabel>
                </CartNavButton>
              </AccountCluster>
            </>
          ) : (
            <>
              <AccountCluster>
                <LanguageSwitcher compact />
                <GuestAuthButtons>
                  <CreateAccountNavButton to="/register">{t('nav.createAccount')}</CreateAccountNavButton>
                  <LoginNavButton to="/login">{t('nav.login')}</LoginNavButton>
                </GuestAuthButtons>
                <CartNavButton to="/cart" aria-label={t('nav.cart')}>
                  <IconWrapper>
                    <Icon>🛒</Icon>
                    {cartItemsCount > 0 && (
                      <Badge>{cartItemsCount > 99 ? '99+' : cartItemsCount}</Badge>
                    )}
                  </IconWrapper>
                  <CartNavLabel>{t('nav.cart')}</CartNavLabel>
                </CartNavButton>
              </AccountCluster>
            </>
          )}
        </UserMenu>
        <HamburgerWrapper>
          <HamburgerButton
            type="button"
            onClick={() => {
              setDropdownOpen(false);
              setSideMenuOpen(true);
            }}
            aria-label="Open menu"
            aria-expanded={sideMenuOpen}
          >
            <HamburgerLine />
            <HamburgerLine />
            <HamburgerLine />
          </HamburgerButton>
          <SideMenu
            isOpen={sideMenuOpen}
            onToggle={setSideMenuOpen}
            isAuthenticated={isAuthenticated}
            isCourier={isCourier}
            isAdmin={isAdmin}
            onLogout={handleLogout}
          />
        </HamburgerWrapper>
      </Nav>
      {showGlobalDepartmentBar ? (
        <DepartmentBar aria-label={t('nav.shopByDepartment')}>
          <DepartmentBarInner>
            <DepartmentLabel>{t('nav.shopByDepartment')}</DepartmentLabel>
            <DepartmentChips>
              {DEPARTMENT_LINKS.map((department) => (
                <DepartmentChip
                  key={department.value}
                  to={getDepartmentHref(department.value)}
                  $active={activeDepartment === department.value}
                  aria-current={activeDepartment === department.value ? 'page' : undefined}
                >
                  {t(department.labelKey)}
                </DepartmentChip>
              ))}
            </DepartmentChips>
          </DepartmentBarInner>
        </DepartmentBar>
      ) : null}
    </HeaderContainer>
  );
};

export default Header;
